import React, { useState } from 'react';
import {
  Box,
  Heading,
  Text,
  SimpleGrid,
  Icon,
  HStack,
  Divider,
  Link,
  Flex,
  Badge,
  Container,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  FaUtensils,
  FaStore,
  FaCoffee,
  FaStar,
  FaWalking,
  FaMapMarkerAlt,
} from 'react-icons/fa';

// 地點類型圖標映射
const placeTypeIcons = {
  restaurant: FaUtensils,
  store: FaStore,
  cafe: FaCoffee,
};

// 地點類型文字映射
const placeTypeLabels = {
  restaurant: '餐廳',
  store: '商店',
  cafe: '咖啡店',
};

// 預定的標籤順序
const tabOrder = ['restaurant', 'store', 'cafe'];

// 價格等級顯示
const renderPriceLevel = (priceLevel) => {
  if (priceLevel === null || priceLevel === undefined) return null;

  const level = parseInt(priceLevel);
  const dollarSigns = Array(level).fill('$').join('');

  return (
    <Badge
      colorScheme={level <= 2 ? 'green' : level === 3 ? 'yellow' : 'red'}
      fontSize='xs'>
      {dollarSigns}
    </Badge>
  );
};

const NearbyPlaces = ({ buildingName, buildingLat, buildingLng, nearbyPlaces }) => {
  // 背景色和邊框顏色
  const bgColor = useColorModeValue('gray.50', 'gray.700');
  const cardBg = useColorModeValue('white', 'gray.600');
  const borderColor = useColorModeValue('gray.200', 'gray.500');
  
  if (!nearbyPlaces || nearbyPlaces.length === 0) {
    return null;
  }

  // 根據類型分組周邊地點
  const placesByType = {};
  nearbyPlaces.forEach((place) => {
    if (!placesByType[place.place_type]) {
      placesByType[place.place_type] = [];
    }
    placesByType[place.place_type].push(place);
  });

  // 按照預定順序排序標籤
  const orderedTabs = tabOrder.filter(type => placesByType[type] && placesByType[type].length > 0);
  
  // 確保至少有一個標籤
  if (orderedTabs.length === 0) {
    return null;
  }

  return (
    <Box p={2} pt="36px" bg={bgColor} my={2}>
      <Container maxW='1120px' borderRadius='md'>
        <Heading as='h2' size='lg' mb={4}>
          {buildingName} 周邊景點
        </Heading>

        {/* 使用 Tabs 組件，但仍保留所有內容對搜索引擎可見 */}
        <Tabs variant="soft-rounded" colorScheme="blue" defaultIndex={0}>
          <TabList>
            {orderedTabs.map((placeType) => (
              <Tab key={placeType}>
                <HStack spacing={2}>
                  <Icon as={placeTypeIcons[placeType]} />
                  <Text fontSize={{ base: '12px', md: '16px' }}>{placeTypeLabels[placeType]} ({placesByType[placeType].length})</Text>
                </HStack>
              </Tab>
            ))}
          </TabList>

          <TabPanels>
            {orderedTabs.map((placeType) => (
              <TabPanel key={placeType} px={0}>
                {/* SEO-friendly 標題 - 對搜索引擎可見 */}
                <Heading as='h3' size='md' mb={4} display="none">
                  附近{placeTypeLabels[placeType]}
                </Heading>
                
                <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                  {placesByType[placeType].map((place) => (
                    <Box
                      key={place.place_id}
                      p={4}
                      borderWidth='1px'
                      borderRadius='md'
                      shadow='sm'
                      bg={cardBg}
                      borderColor={borderColor}
                      transition='all 0.2s'
                      _hover={{ shadow: 'md' }}>
                      <Flex justify='space-between' align='center'>
                        <Heading as='h4' size='sm' noOfLines={1} flex='1'>
                          {place.name}
                        </Heading>
                        {renderPriceLevel(place.price_level)}
                      </Flex>

                      <Text
                        fontSize='sm'
                        color='gray.600'
                        mt={1}
                        mb={2}
                        noOfLines={2}>
                        <Icon
                          as={FaMapMarkerAlt}
                          color='gray.400'
                          mr={1}
                          boxSize='3'
                        />
                        {place.vicinity}
                      </Text>

                      <HStack mt={2} spacing={3} wrap='wrap'>
                        {place.rating && (
                          <HStack spacing={1}>
                            <Icon as={FaStar} color='yellow.400' />
                            <Text fontSize='sm'>
                              {place.rating} ({place.user_ratings_total})
                            </Text>
                          </HStack>
                        )}

                        <HStack spacing={1}>
                          <Icon as={FaWalking} color='green.500' />
                          <Text fontSize='sm'>{place.distance_meters}公尺</Text>
                        </HStack>
                      </HStack>

                      {/* 步行導航連結 */}
                      <Link
                        href={`https://www.google.com/maps/dir/?api=1&origin=${buildingLat || ''}, ${buildingLng || ''}&destination=${place.lat},${place.lng}&destination_place_id=${place.place_id}&travelmode=walking`}
                        isExternal
                        display='block'
                        fontSize='xs'
                        color='blue.500'
                        mt={2}
                        textAlign='right'>
                        走路導航
                      </Link>
                    </Box>
                  ))}
                </SimpleGrid>
              </TabPanel>
            ))}
          </TabPanels>
        </Tabs>

        <Divider my={4} />

        <Text fontSize='sm' color='gray.500' textAlign='center'>
          以上資訊僅供參考，實際營業狀況請以各店家公告為準
        </Text>
      </Container>
    </Box>
  );
};

export default NearbyPlaces;